/**
 * A field's problem under it (a client-side check or the server's 422 for that field) and the attributes that tie
 * it to its input. The pair is keyed by the input's id, so the error's id cannot drift from what the input names:
 *
 *   <input id="x" {...fieldA11y('x', error)} />
 *   <FieldError inputId="x" message={error} />
 */

import { CircleAlert } from 'lucide-react';

/** The id of the error shown under the input `inputId`. */
export function fieldErrorId(inputId: string): string {
  return `${inputId}-error`;
}

/**
 * `aria-invalid` and `aria-describedby` for the input `inputId` while it has `error`: the error's id joins the ids in
 * `describedBy` (a hint, a counter), which stay when there is none.
 */
export function fieldA11y(
  inputId: string,
  error: string | null | undefined,
  describedBy?: string,
): { 'aria-invalid': true | undefined; 'aria-describedby': string | undefined } {
  const ids = [describedBy, error ? fieldErrorId(inputId) : undefined].filter(Boolean).join(' ');
  return { 'aria-invalid': error ? true : undefined, 'aria-describedby': ids || undefined };
}

/** The error under the input `inputId`, with an icon so colour is not the only cue. Nothing when there is none. */
export function FieldError({ inputId, message }: Readonly<{ inputId: string; message: string | null | undefined }>) {
  if (!message) return null;
  return (
    <span id={fieldErrorId(inputId)} className="field-error" role="alert">
      <CircleAlert aria-hidden="true" />
      {message}
    </span>
  );
}

/**
 * Why a disabled Save button is disabled, as visible text near it: pass its `id` to the button's `describedBy`.
 * Nothing while `show` is false.
 */
export function SaveBlockedNote({ id, show, children = 'Fix the highlighted fields to save.' }: Readonly<{ id: string; show: boolean; children?: string }>) {
  if (!show) return null;
  return (
    <p id={id} className="hint">
      {children}
    </p>
  );
}
